import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './PageTransitionSkeleton.module.css';

export default function PageTransitionSkeleton() {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const isInitialMount = useRef(true);
  const currentPath = useRef(location.pathname);

  useEffect(() => {
    // Skip on initial page mount (handled by SplashScreen)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      currentPath.current = location.pathname;
      return;
    }

    if (currentPath.current === location.pathname) {
      return;
    }
    currentPath.current = location.pathname;

    // Trigger skeleton loading state
    setIsTransitioning(true);
    setIsFadingOut(false);

    let dismissed = false;
    let fadeOutTimer = null;
    let unmountTimer = null;

    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      // Brief pause to ensure the video has painted its active motion frame behind the veil
      fadeOutTimer = setTimeout(() => {
        setIsFadingOut(true);
        unmountTimer = setTimeout(() => {
          setIsTransitioning(false);
          setIsFadingOut(false);
        }, 360); // Matches CSS transition duration
      }, 70);
    };

    const handleMotionReady = () => {
      dismiss();
    };

    window.addEventListener('church:motion-ready', handleMotionReady);

    // Safety fallback timeout in case browser blocks video autoplay (e.g. low power mode)
    const safetyTimeout = setTimeout(() => {
      dismiss();
    }, 1600);

    return () => {
      window.removeEventListener('church:motion-ready', handleMotionReady);
      clearTimeout(safetyTimeout);
      if (fadeOutTimer) clearTimeout(fadeOutTimer);
      if (unmountTimer) clearTimeout(unmountTimer);
    };
  }, [location.pathname]);

  if (!isTransitioning) return null;

  const isLeadership = location.pathname.includes('/leadership');

  return (
    <div
      className={`${styles.skeletonContainer} ${isFadingOut ? styles.fadeOut : ''}`}
      aria-hidden="true"
      role="status"
      aria-label="Loading page content"
    >
      {/* Shimmering Top Progress Indicator */}
      <div className={styles.progressBar} />

      <div className={styles.skeletonContent}>
        {/* Top Hero / Video Banner Skeleton */}
        <div className={`${styles.heroSkeleton} ${styles.shimmerItem}`}>
          <div className={styles.heroContentSkeleton}>
            <div className={`${styles.badgeSkeleton} ${styles.shimmerItem}`} />
            <div className={`${styles.titleSkeletonLg} ${styles.shimmerItem}`} />
            <div className={`${styles.titleSkeletonMd} ${styles.shimmerItem}`} />
            <div className={styles.btnGroupSkeleton}>
              <div className={`${styles.btnSkeleton} ${styles.shimmerItem}`} />
              <div className={`${styles.btnSkeleton} ${styles.shimmerItem}`} />
            </div>
          </div>
        </div>

        {/* Dynamic Body Content Skeleton */}
        <div className={styles.bodySkeleton}>
          {isLeadership ? (
            <>
              {/* Pastor Tabs Switcher Skeleton */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginBottom: '2rem',
                }}
              >
                <div style={{ width: '220px', height: '64px', borderRadius: '20px' }} className={`${styles.shimmerItem}`} />
                <div style={{ width: '220px', height: '64px', borderRadius: '20px' }} className={`${styles.shimmerItem}`} />
                <div style={{ width: '220px', height: '64px', borderRadius: '20px' }} className={`${styles.shimmerItem}`} />
              </div>

              {/* Pastor Speech Card Skeleton */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '300px 1fr',
                  gap: '2.5rem',
                  padding: '2.5rem',
                  background: '#f8fafc',
                  borderRadius: '24px',
                  border: '1.5px solid #e2e8f0',
                }}
              >
                <div style={{ height: '360px', borderRadius: '18px' }} className={`${styles.shimmerItem}`} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                  <div style={{ width: '40%', height: '28px', borderRadius: '6px' }} className={`${styles.shimmerItem}`} />
                  <div style={{ width: '75%', height: '34px', borderRadius: '8px' }} className={`${styles.shimmerItem}`} />
                  <div style={{ width: '100%', height: '18px', borderRadius: '4px' }} className={`${styles.shimmerItem}`} />
                  <div style={{ width: '92%', height: '18px', borderRadius: '4px' }} className={`${styles.shimmerItem}`} />
                  <div style={{ width: '85%', height: '18px', borderRadius: '4px' }} className={`${styles.shimmerItem}`} />
                  <div style={{ width: '60%', height: '18px', borderRadius: '4px' }} className={`${styles.shimmerItem}`} />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Standard Cards Grid Skeleton */}
              <div className={styles.cardGridSkeleton}>
                <div className={styles.cardSkeleton}>
                  <div className={`${styles.textLineLg} ${styles.shimmerItem}`} />
                  <div className={`${styles.textLineMd} ${styles.shimmerItem}`} />
                  <div className={`${styles.textLineSm} ${styles.shimmerItem}`} />
                </div>
                <div className={styles.cardSkeleton}>
                  <div className={`${styles.textLineLg} ${styles.shimmerItem}`} />
                  <div className={`${styles.textLineMd} ${styles.shimmerItem}`} />
                  <div className={`${styles.textLineSm} ${styles.shimmerItem}`} />
                </div>
              </div>

              {/* Media / Video Showcase Skeleton Box */}
              <div className={`${styles.mediaSkeleton} ${styles.shimmerItem}`} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
